import React from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'sonner';

import {
  Step, StepContent, StepLabel, Stepper, Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import Button from 'creative-components/CustomButtons/Button';

import { DASHBOARD_ROUTE } from 'components/AuthDataProvider/AuthDataProvider';
import { useLoadingIndicatorContext } from 'components/LoadingIndicator/LoadingIndicatorProvider';
import LaunchStepIcon from 'components/SimpleLaunches/LaunchStepIcon';
import PublicLaunchIntro from 'components/SimpleLaunches/PublicLaunchIntro';
import SimpleCreateCampaignStep, { type CampaignCreateData } from 'components/SimpleLaunches/SimpleCampaign/SimpleCreateCampaignStep';
import SimpleCreateAccountStep from 'components/SimpleLaunches/SimpleCreateAccountStep';
import { useUserInfoContext } from 'components/UserInfoProvider/UserInfoProvider';

import analyticsClient from 'utils/analytics';
import { createSimpleCampaign, saveSimpleCampaignDraft } from 'utils/api';
import { showAPIErrorAlert } from 'utils/lib';

const useStyles = makeStyles((theme) => ({
  root: {
    margin: '0 auto',
    padding: '24px 30px 12px',
    position: 'relative',

    [theme.breakpoints.down('xs')]: {
      padding: 0,
    },

    '@media (min-width: 600px)': {
      width: '580px',
    },
    '@media (min-width: 900px)': {
      width: '840px',
    },
    '@media (min-width: 1200px)': {
      width: '1104px',
    },
    '@media (min-width: 1440px)': {
      width: '1344px',
    },
  },
  stepLabel: {
    fontSize: '18px',
  },
  stepper: {
    '& .MuiStepConnector-vertical': {
      marginLeft: '17px',
    },
  },
  stepDescription: {
    marginBottom: '20px',
  },
  stepIconContainer: {
    paddingRight: '14px',
  },
  stepContent: {
    marginLeft: '17px',
    paddingLeft: '24px',
  },
  goToDashboardButton: {
    marginTop: '20px',
    float: 'right',
  },
}));

const steps: { [key: string]: string } = {
  'Choose farm': 'Select the farm area you want to target and generate your estimated campaign costs.',
  'Create account': 'Review details required for your Harvist account',
  'Campaign Started': 'Your campaign has been created and will start shortly! Give us some time to crunch the data and prepare everything for you.',
};

const SimpleCreateCampaignPage = () => {
  const classes = useStyles();
  const history = useHistory();
  const { userInfo } = useUserInfoContext();
  const { showLoadingIndicatorModal, hideLoadingIndicatorModal } = useLoadingIndicatorContext() as any;

  const [activeStep, setActiveStep] = React.useState(0);
  const [paymentMethodId, setPaymentMethodId] = React.useState('');
  const [isAnnualBilling, setIsAnnualBilling] = React.useState(false);
  const [createCampaignData, setCreateCampaignData] = React.useState<CampaignCreateData | undefined>();

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  React.useEffect(() => {
    analyticsClient.track('simple_campaign_creation_started');
  }, []);

  return (
    <div className={classes.root}>
      <PublicLaunchIntro
        label="How This Works"
        title="Launch Your Farming Campaign"
        steps={[
          'Choose the farm area you want to target.',
          'Create your account and add billing details.',
          'Create the campaign and start sending marketing touches to your audience!',
        ]}
        reassuranceText="Your campaign starts and you can access your farm data. You can view, customize, and see engagements on the touches in your dashboard."
      />
      <Stepper activeStep={activeStep} orientation="vertical" className={classes.stepper}>
        {Object.keys(steps).map((label) => (
          <Step key={label}>
            <StepLabel
              StepIconComponent={LaunchStepIcon}
              classes={{ label: classes.stepLabel, iconContainer: classes.stepIconContainer }}
            >
              {label}
            </StepLabel>
            <StepContent className={classes.stepContent}>
              <Typography className={classes.stepDescription}>{steps[label]}</Typography>
              {activeStep === 0 && (
                <SimpleCreateCampaignStep
                  handleNext={handleNext}
                  setCreateCampaignData={setCreateCampaignData}
                  isAnnualBilling={isAnnualBilling}
                  setIsAnnualBilling={setIsAnnualBilling}
                />
              )}

              {activeStep === 1 && (
                <SimpleCreateAccountStep
                  submitButtonText="Start Farming"
                  secondaryActionText={userInfo ? 'Save as Draft' : undefined}
                  paymentMethodId={paymentMethodId}
                  setPaymentMethodId={setPaymentMethodId}
                  handleNext={handleNext}
                  onLaunch={async () => {
                    await createSimpleCampaign({
                      ...createCampaignData!,
                      paymentMethodId,
                    });

                    await analyticsClient.track('simple_campaign_creation_completed');
                  }}
                  onSecondaryAction={userInfo ? async () => {
                    showLoadingIndicatorModal();

                    try {
                      await saveSimpleCampaignDraft(createCampaignData!);
                      history.push(DASHBOARD_ROUTE);
                      toast.success('Your campaign draft has been saved. You can return to this campaign to pick up where you left off in the future.');
                    } catch (err) {
                      console.error('error', err);
                      showAPIErrorAlert(err);
                    } finally {
                      hideLoadingIndicatorModal();
                    }
                  } : undefined}
                />
              )}

              {activeStep === 2 && (
                <Button
                  round
                  color="primary"
                  className={classes.goToDashboardButton}
                  onClick={() => history.push(DASHBOARD_ROUTE)}
                >
                  Go to My Dashboard
                </Button>
              )}
            </StepContent>
          </Step>
        ))}
      </Stepper>
    </div>
  );
};

export default SimpleCreateCampaignPage;
